export interface InlineSelectOption<T = string> {
  label: string;
  value: T;
}

interface InlineSelectInputProps<T = string> {
  options: InlineSelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  name: string;
  label?: string;
  required?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  tooltip?: string;
  align?: 'center' | 'left';
}

/**
 * Composant de sélection inline avec style badge (similaire à MultiSelectInput)
 * Utilisé pour des choix exclusifs (comportement radio)
 */
import { Tooltip } from '@/Components/ui/Tooltip';

export const InlineSelectInput = <T extends string = string>({
  options,
  value,
  onChange,
  name,
  label,
  required = false,
  className = '',
  size = 'sm',
  tooltip,
  align = 'center',
}: InlineSelectInputProps<T>) => {
  const padClass = size === 'md' ? 'tw-px-4 tw-py-2.5' : 'tw-px-2.5 tw-py-1.5';
  const textClass = size === 'md' ? 'tw-text-sm' : 'tw-text-xs';
  return (
    <div className={className}>
      {label && (
        <h4 className={`tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 ${align === 'left' ? 'tw-text-left' : 'tw-text-center'}`}>
          {label}
          {!required && <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1">(Optionnel)</span>}
          {tooltip && <Tooltip content={tooltip} />}
        </h4>
      )}

      <div className={`tw-flex tw-gap-2 ${align === 'left' ? 'tw-justify-start' : 'tw-justify-center'}`}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={String(option.value)}
              className={`
                tw-flex tw-items-center tw-gap-1.5 ${padClass}
                tw-border-2 tw-rounded-md tw-cursor-pointer
                tw-transition-all tw-select-none
                ${isSelected
                  ? 'tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7] tw-border-white tw-text-white tw-shadow-md'
                  : 'tw-bg-white tw-border-gray-300 tw-text-gray-700 hover:tw-border-gray-400 hover:tw-shadow-md'
                }
              `}
            >
              <input
                type="radio"
                name={name}
                value={String(option.value)}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="tw-sr-only"
              />
              <span className={`tw-font-medium ${textClass}`}>{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
